import { useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useInfiniteQuery, useQuery, useQueryClient, InfiniteData, useIsFetching } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { QUERY_KEYS, isValidUUID } from '@restworld/utils-common';
import {
  IndexJobApplicationsParams,
  IndexJobApplicationsReturnType,
  useWorkerJobPositionsService,
} from '@restworld/data-services';
import { JobApplication } from '@restworld/utility-types';

function useEmployerJobApplicationsQuery(params?: IndexJobApplicationsParams) {
  const workerJobPositionsService = useWorkerJobPositionsService();
  const queryKey = useEmployerJobApplicationQueryKey();
  const query = useQuery({
    queryKey,
    queryFn: async () =>
      (await workerJobPositionsService.indexJobApplicationsGraphql(params)).data.data.index_job_applications.data,
    staleTime: 10000,
    enabled: !!queryKey[1] && isValidUUID(queryKey[1] as string),
  });
  return { jobApplications: query?.data, query };
}
export default useEmployerJobApplicationsQuery;

export const useEmployerJobApplicationQueryKey = () => {
  const router = useRouter();
  const { id: jobPositionId } = router.query;

  return [QUERY_KEYS.WORKER_JOB_POSITIONS.APPLICATIONS, jobPositionId];
};

export const useEmployerJobApplications = () => {
  const employerJobApplicationQueryKey = useEmployerJobApplicationQueryKey();
  const jobApplications =
    (useQueryClient().getQueryData(employerJobApplicationQueryKey) as IndexJobApplicationsReturnType['data']) || [];
  return jobApplications;
};

export const useEmployerJobApplicationsLoading = () => {
  const queryKey = useEmployerJobApplicationQueryKey();

  // useIsFetching is REACTIVE - it will cause re-renders when the fetching state changes
  const isFetching = useIsFetching({ queryKey });

  // Also check if we have data already (for initial load vs refetch)
  const queryClient = useQueryClient();
  const hasData = !!queryClient.getQueryData(queryKey);

  // Only consider "loading" if we're fetching AND don't have data yet
  return isFetching > 0 && !hasData;
};

export const useDiscardJobApplicationQueryKey = () => {
  const { id: jobPositionId } = useRouter().query;
  return [QUERY_KEYS.WORKER_JOB_POSITIONS.APPLICATIONS, 'worker_jp_discarded', jobPositionId];
};

export const useDiscardedJobApplicationsQuery = (params?: IndexJobApplicationsParams) => {
  const workerJobPositionsService = useWorkerJobPositionsService();
  const discardQueryKey = useDiscardJobApplicationQueryKey();

  useQuery({
    queryKey: discardQueryKey,
    queryFn: async () =>
      (await workerJobPositionsService.indexJobApplicationsGraphql(params)).data.data.index_job_applications.data,
    staleTime: 10000,
    enabled: !!discardQueryKey[2] && isValidUUID(discardQueryKey[2] as string),
  });
};

export const useDiscardedJobApplications = () => {
  const queryKey = useDiscardJobApplicationQueryKey();
  const queryClient = useQueryClient();
  const discardedJobApplications = (queryClient.getQueryData(queryKey) as IndexJobApplicationsReturnType['data']) || [];

  return discardedJobApplications;
};

export const usePreselectionRejectedJobApplicationsQueryKey = () => {
  const { id: jobPositionId } = useRouter().query;
  return [QUERY_KEYS.WORKER_JOB_POSITIONS.APPLICATIONS, 'preselection_rejected', jobPositionId];
};

export const usePreselectionRejectedJobApplicationsQuery = (params?: IndexJobApplicationsParams) => {
  const workerJobPositionsService = useWorkerJobPositionsService();
  const queryKey = usePreselectionRejectedJobApplicationsQueryKey();

  useQuery({
    queryKey,
    queryFn: async () =>
      (await workerJobPositionsService.indexJobApplicationsGraphql(params)).data.data.index_job_applications.data,
    staleTime: 10000,
    enabled: !!queryKey[2] && isValidUUID(queryKey[2] as string),
  });
};

export const usePreselectionRejectedJobApplications = () => {
  const queryKey = usePreselectionRejectedJobApplicationsQueryKey();
  const queryClient = useQueryClient();
  const data = (queryClient.getQueryData(queryKey) as IndexJobApplicationsReturnType['data']) || [];
  return data;
};

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;

export function useEmployerJobApplicationsInfiniteQuery(
  params?: Omit<IndexJobApplicationsParams, 'offset'>,
  staleTime?: number
) {
  const workerJobPositionsService = useWorkerJobPositionsService();

  const queryKey = useEmployerJobApplicationsInfiniteQueryKey(params);

  const nextPageRef = useRef<number>();
  const hasNextPageRef = useRef<boolean>();
  const { limit = DEFAULT_LIMIT, ...restParams } = params || {};

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return workerJobPositionsService.indexJobApplicationsGraphql({
        limit,
        offset: pageParam * limit,
        ...restParams,
      });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.data?.data?.index_job_applications.hits;
      const applicationsLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = applicationsLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    staleTime,
  });

  const jobApplicationsInfo = useMemo(() => extractJobApplicationsInfo(query.data), [query.data]);

  return {
    query,
    jobApplications: jobApplicationsInfo.items,
    hits: jobApplicationsInfo.hits,
    hasNextPage: hasNextPageRef.current,
  };
}

export function useEmployerJobApplicationsInfiniteQueryKey(params?: IndexJobApplicationsParams) {
  const paramsString = useMemo(() => (params ? JSON.stringify(params) : ''), [params]);
  const queryKey = useMemo(() => [QUERY_KEYS.WORKER_JOB_POSITIONS.APPLICATIONS, paramsString], [paramsString]);
  return queryKey;
}

type JobApplicationsInfo = {
  items: JobApplication[];
  hits: number;
};

export function extractJobApplicationsInfo(
  data?: InfiniteData<AxiosResponse<{ data: { index_job_applications: IndexJobApplicationsReturnType } }, unknown>>
): JobApplicationsInfo {
  if (!data || !data.pages) return { items: [], hits: 0 };
  const items = data?.pages.flatMap((page) => page.data?.data?.index_job_applications.data);

  const hits = data?.pages[0]?.data?.data?.index_job_applications.hits;

  return { items, hits };
}
